export interface ProductPrices {
  maxPrice: string;
  minPrice: string;
}

export type Variant = {
  id: string;
  availableForSale: boolean;
  quantityAvailable: number;
  options: { name: string; value: string; id: string }[];
};

export enum ProductType {
  TShirt = 'T-Shirt',
  ZipUp = 'Zip up',
  VarsityJacket = 'Varsity Jacket',
  CroppedZipUp = 'Cropped Zip up',
  Hoodie = 'Hoodie',
  LongSleeve = 'Long Sleeve',
  Bag = 'Bag',
  Sweatpants = 'Sweatpants',
  WideSweatpants = 'Wide-Leg Sweatpants',
  Joggers = 'Joggers',
  GiftCard = 'Gift Card',
  Mask = 'Mask',
  Crewneck = 'Crewneck',
  Shorts = 'Shorts',
  TankTop = 'Tank Top',
  MensTankTop = 'Mens Tank Top',
  WomensTankTop = 'Womens Tank Top',
  Sunglasses = 'Sunglasses',
  ToteBag = 'Tote Bag',
  Jeans = 'Jeans',
  Socks = 'Socks',
  Cap = 'Cap',
  Rug = 'Rug',
  Sweater = 'Sweater',
  PuffyStarBag = 'Puffy Star Bag',
  TechPants = 'Tech Pants',
  TechJacket = 'Tech Jacket',
  QuarterZipPullover = 'Quarter Zip Pullover',
  Beanie = 'Beanie',
  WashedShorts = 'Washed Shorts',
  WashedSkirt = 'Washed Skirt',
  Skirts = 'Skirts',
  Pants = 'Pants',
  Puffer = 'Puffer',
  StarCamoBeanie = 'Star Camo Beanie',
  FoldoverBeanie = 'Fold-Over Beanie',
  BabyTee = 'Baby Tee',
  BabyTeeReglan = 'Baby Tee Reglan Sleeve',
  CombatPants = 'Combat Pants',
  DenimJacket = 'Denim Jacket',
  PolarFleece = 'Polar Fleece',
  DenimWorkwearJacket = 'Denim Workwear Jacket',
  WorkwearJacket = 'Workwear Jacket',
  Knitwear = 'Knitwear',
  DenimShorts = 'Denim Shorts',
  DenimJeans = 'Denim Jeans',
  DenimCorsetTop = 'Denim Corset Top',
  DenimSkirt = 'Denim Skirt',
  RipstopCamoSkirt = 'Ripstop Camo Skirt',
  CamoThermallongSleeve = 'Camo Thermal Long-Sleeve',
  CargoPants = 'Cargo Pants',
  CamoBikini = 'Camo Bikini',
  WomensWhashedRibbedZipUp = 'Womens Washed Ribbed Zip-Up',
  MensWashedRibbedZipUp = 'Mens Washed Ribbed Zip-Up',
  SweatShorts = 'Sweat Shorts',
  Boxers = 'Boxers',
  RibbedTankTopSizes = 'RibbedTankTopSizes',
  RibbedTankTopSizesWoman = 'RibbedWomanTankTopSizes',
  FootballMeshShorts = 'Football Mesh Shorts',
  FootballJersey = 'Football Jersey',
  WomensFootballJersey = 'Womens Football Jersey',
  HalterTop = "Women's Halter Top",
  StraightLegSweatpants = 'Straight Leg Sweatpants',
  SlinkySkirt = 'Pleated Skirt',
  ButtonUpShirt = 'Button Up Shirt',
  TrackPants = 'Track Pants',
  TrackTop = 'Track Top',
  PerformanceTShirt = 'Performance T-Shirt',
  SweatSkirt = 'Sweat Skirt',
  CroppedBoxyTShirt = 'Cropped Boxy T-Shirt',
  LoungeCorset = 'Lounge Corset',
  WomensShorts = 'Womens Shorts',
  SwimmingShorts = 'Swimming Shorts',
  WideLegDenim = 'Wide Leg Denim',
  LongSleeveWaffle = 'Long Sleeve Waffle',
  RacerJacket = 'Racer Jacket',
  LightWeightTank = 'Lightweight Tank',
  CuffedSweatpants = 'Cuffed Sweatpants',
  NewHoodieShape = 'New Hoodie Shape',
  WomensOneShoulderTop = 'Womens One Shoulder Top',
  VelourSweatpants = 'Velour Sweatpants',
  MeshLayeredTShirt = 'Mesh Layered T-Shirt',
  VelourTrackJacket = 'Velour Track Jacket',
  NewPolarFleece = 'New Polar Fleece',
  Other = 'Other',
  FauxFurZipUpHoodie = 'Faux Fur Zip up Hoodie',
  TopLeagueTrackJacket = 'Top League Track Jacket',
  TopLeagueTrackPants = 'Top League Track Pants',
  VelourTrackPants = 'Velour Track Pants',
  PixelStarBeanie = 'Pixel Star Beanie',
  RacerKnit = 'Racer Knit',
  Jersey = 'Jersey',
  MeshShorts = 'Mesh Shorts',
  CottonTrackTop = 'Cotton Track Top',
  WaffleBeanie = 'Waffle Beanie',
}

export enum ProductMetafields {
  DropStartTime = 'drop_start_time',
  DropEndTime = 'drop_end_time',
  NextDropIn = 'next_drop_in',
  FlashSaleColor = 'flash_sale_color',
  SaleEnded = 'sale_ended',
  NextState = 'next_state',
}

export const FlashSaleStatus = {
  ...ProductMetafields,
} as const;

export interface Product {
  id: string;
  images: string[];
  title: string;
  prices: ProductPrices;
  handle: string;
  description: string;
  descriptionHtml: string;
  productType: ProductType;
  variants: Variant[];
  tags: string[];
  totalInventory?: number;
  collections?: string[];
  metafields?: { key: string; value: string; type: string }[];
}

export const enum ProductStatus {
  Available = 'isAvailable',
}

export type ProductSizes =
  | 'XX-SMALL'
  | 'X-SMALL'
  | 'SMALL'
  | 'MEDIUM'
  | 'LARGE'
  | 'X-LARGE'
  | 'XX-LARGE'
  | 'XXX-LARGE'
  | 'One Size';
